import React, { useState } from 'react';
import styled from 'styled-components';
import Slot from '@solvhealth/types/interfaces/Location/Slot';
import Loading from '../SolvPatternLibrary/Loading';
import TimeSelectorModal, {
  WIDGET_BOOKING_TIME_SELECTOR_MODAL,
} from './SlotSelector/components/TimeSelectorModal';

import TabView from './Views/TabView';
import StackedView from './Views/StackedView';
import { colors } from '../../constants/colors';
import { safeGet } from '../../core/util/object';
import { isTabletOrSmaller } from '../../core/util/device/screen';
import useSlotsData from './hooks/useSlotsData';
import { EN, SupportedLocale } from '../../core/util/localizedStr';
import { Viewport } from '~/components/useViewport';

const SpacedLoading = styled.div.withConfig({ componentId: 'sc-1i2j2hs-0' })`
  text-align: center;
  height: 144px;
  align-items: center;
  justify-content: center;
  display: flex;
`;

type Props = {
  location?: any;
  handleSelectSlot?: (...args: any[]) => any;
  handleSlotGridChange?: (...args: any[]) => any;
  isTabView?: any; // TODO: PropTypes.booBookingWidget / Desktop / DesktopBookingWidgetForm.tsxl
  selectedSlots?: Slot[];
  handleSelectedDate?: (...args: any[]) => any;
  showModal?: (modalId: string) => void;
  modalState?: any;
  hideModal?: (modalId: string) => void;
  setSelectedSlot?: (...args: any[]) => any;
  shouldShowWalkInCopy?: boolean;
  viewport?: Viewport;
  blurField?: (...args: any[]) => any;
  selectedDate?: any;
  setSelectedDate?: (...args: any[]) => any;
  visible?: boolean;
  onModalSubmit?: () => void;
  modalButtonType?: 'button' | 'submit' | 'reset';
  locale?: SupportedLocale;
  isCTA?: boolean;
  // below props are for old booking flow
  shouldSelectFirstSlotAsInitialValue: boolean;
};

const TimeSelector = (props: Props) => {
  const {
    location,
    handleSelectSlot,
    isTabView = true,
    selectedSlots = [],
    showModal,
    modalState,
    hideModal,
    setSelectedSlot,
    shouldShowWalkInCopy,
    viewport,
    blurField,
    selectedDate,
    setSelectedDate,
    visible = true,
    onModalSubmit,
    modalButtonType,
    locale = EN,
    isCTA = false,
    // below props are for old booking flow
    shouldSelectFirstSlotAsInitialValue,
  } = props;

  const [hasOpenedTimeSelectorModal, setHasOpenedTimeSelectorModal] = useState(false);

  const slots = safeGet(location)('slots');

  const { handleDateChange, futureSlotData, isMoreDate, nextDate, setIsMoreDate } = useSlotsData(
    location,
    selectedDate,
    selectedSlots,
    setSelectedDate
  );

  if (!visible) return null;

  if (!slots || !selectedDate || !nextDate) {
    return (
      <SpacedLoading>
        <Loading color={colors.crunchBerry} />
      </SpacedLoading>
    );
  }

  const shouldShowModal = safeGet(modalState, false)(WIDGET_BOOKING_TIME_SELECTOR_MODAL);

  return (
    <div data-testid="booking-time-selector">
      {isTabView ? (
        <TabView
          futureSlotData={futureSlotData}
          handleSelectSlot={handleSelectSlot!}
          isCTA={isCTA}
          isMoreDate={isMoreDate}
          locale={locale}
          location={location}
          nextDate={nextDate}
          selectedDate={selectedDate}
          selectedSlots={selectedSlots}
          setAndHandleDate={handleDateChange}
          setIsMoreDate={setIsMoreDate}
          shouldSelectFirstSlotAsInitialValue={shouldSelectFirstSlotAsInitialValue}
        />
      ) : (
        <StackedView
          futureSlotData={futureSlotData}
          handleSelectSlot={handleSelectSlot}
          hasOpenedTimeSelectorModal={hasOpenedTimeSelectorModal}
          isCTA={isCTA}
          isModalOpen={shouldShowModal}
          isMoreDate={isMoreDate}
          locale={locale}
          location={location}
          modalState={modalState}
          nextDate={nextDate}
          selectedDate={selectedDate}
          selectedSlots={selectedSlots}
          setAndHandleDate={handleDateChange}
          setHasOpenedTimeSelectorModal={setHasOpenedTimeSelectorModal}
          setIsMoreDate={setIsMoreDate}
          shouldShowWalkInCopy={shouldShowWalkInCopy}
          showModal={showModal}
        />
      )}
      {shouldShowModal && (
        <TimeSelectorModal
          blurField={blurField}
          buttonType={modalButtonType}
          fullScreen={isTabletOrSmaller(viewport?.width)}
          futureSlotData={futureSlotData}
          handleSelectSlot={handleSelectSlot}
          isMoreDate={isMoreDate}
          locale={locale}
          location={location}
          nextDate={nextDate}
          onClose={hideModal}
          onModalSubmit={onModalSubmit}
          open={shouldShowModal}
          selectedDate={selectedDate}
          selectedSlots={selectedSlots}
          setAndHandleDate={handleDateChange}
          setIsMoreDate={setIsMoreDate}
          setSelectedSlot={setSelectedSlot}
          shouldSelectFirstSlotAsInitialValue={shouldSelectFirstSlotAsInitialValue}
          shouldShowWalkInCopy={shouldShowWalkInCopy}
        />
      )}
    </div>
  );
};

export default TimeSelector;
